import { CommandSystem, CommandSystemDonate, CommandSystemExit, isCommandSystemDonate, isCommandSystemExit } from './framework/types/commands';
import { Bridge } from './framework/types/modules';

const API_URL = 'https://api.digital-kaleidoscope.org/donate';
const FIVE_MINUTES_MS = 300_000;

declare global {
  interface Window {
    submissionId: number | undefined;
    submissionError: {
      message: string;
      code: string;
      timestamp: string;
    } | undefined;
  }
}

function generateSubmissionId(): number {
  return Math.floor(1_000_0000_0000_0000 + Math.random() * 9_000_0000_0000_0000);
}

interface DataItem {
  id: string;
  title: { en: string; nl: string };
  data_frame: any;
}

export default class FakeBridge implements Bridge {
  send(command: CommandSystem): void {
    if (isCommandSystemDonate(command)) {
      this.handleDonation(command);
    } else if (isCommandSystemExit(command)) {
      this.handleExit(command);
    }
  }

  async handleDonation(command: CommandSystemDonate): Promise<void> {
    try {
      const data = JSON.parse(command.json_string);

      const isTikTok = command.key.endsWith('-TikTok') || command.key.endsWith('-Tiktok');
      const isActivityWatch = command.key.endsWith('-ActivityWatch') || command.key.endsWith('-Activitywatch');

      if (!isTikTok && !isActivityWatch) return;

      const platform = isTikTok ? 'TikTok' : 'ActivityWatch';

      if (!window.submissionId) {
        window.submissionId = generateSubmissionId();
      }

      const filteredData = data.filter((item: DataItem) => item.id !== 'metadata');

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: window.submissionId,
          platform,
          data: filteredData,
        }),
        signal: AbortSignal.timeout(FIVE_MINUTES_MS),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server error ${response.status}: ${text}`);
      }

      (globalThis as any).userDonated = true;

      this.handleExit({
        __type__: 'CommandSystemExit',
        code: 0,
        info: window.submissionId.toString(),
      });
    } catch (error: unknown) {
      const err = error as Error;
      window.submissionError = {
        message: err.message,
        code: 'upload_failed',
        timestamp: new Date().toLocaleString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        }),
      };
      this.handleExit({
        __type__: 'CommandSystemExit',
        code: 1,
        info: window.submissionId?.toString() ?? 'error',
      });
    }
  }

  handleExit(command: CommandSystemExit): void {
    this.triggerVoidPayload();
  }

  private triggerVoidPayload(): void {
    const workerEngine = (window as any).workerEngine;
    if (workerEngine?.worker) {
      workerEngine.worker.postMessage({ eventType: 'showEndPage' });
    }
  }
}
